import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import DOMPurify from 'dompurify';
import { SelectInput, Dialog, Icon } from 'uxd-design-system-components';
import './LanguageSelect.scss';
import { LocaleContext } from '../../contexts';
import getText from '../../assets/locale/locale';
import { LOCALE_EN_CA, LOCALE_FR_CA } from '../../constants/constants';

const LanguageSelect = (props) => {
  const { addBeforeUnloadEventListener, removeBeforeUnloadEventListener } =
    props;

  const locId = useContext(LocaleContext);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [options, setOptions] = useState([
    {
      name: '',
      selected: false,
      value: '',
      disabled: false,
    },
  ]);
  const [url, setUrl] = useState('');

  const INTEG_GATEWAY_URL_REGEX = /.apps2.devjones.com/;
  const UAT_GATEWAY_URL_REGEX = /.apps2.uat.edwardjones.com/;
  const PROD_GATEWAY_URL_REGEX = /.apps2.edwardjones.com/;
  const UAT_LANGUAGE_URL_REGEX = /.uat.edwardjones.ca/;
  const PROD_LANGUAGE_URL_REGEX = /.edwardjones.ca/;
  const UAT_EXTERNAL_TEST_URL_REGEX = /-uat.testjones.net/;
  const PREPROD_EXTERNAL_TEST_URL_REGEX = /.testjones.net/;

  const getSelectLanguageUrls = () => {
    const { hostname } = window.location;
    if (INTEG_GATEWAY_URL_REGEX.test(hostname)) {
      return `https://prp-startingpoint-gtwy.apps2.devjones.com?locale=${LOCALE_EN_CA},https://prp-startingpoint-gtwy.apps2.devjones.com?locale=${LOCALE_FR_CA}`;
    }
    if (UAT_GATEWAY_URL_REGEX.test(hostname)) {
      return `https://prp-startingpoint-gtwy.apps2.uat.edwardjones.com?locale=${LOCALE_EN_CA},https://prp-startingpoint-gtwy.apps2.uat.edwardjones.com?locale=${LOCALE_FR_CA}`;
    }
    if (PROD_GATEWAY_URL_REGEX.test(hostname)) {
      return `https://prp-startingpoint-gtwy.apps2.edwardjones.com?locale=${LOCALE_EN_CA},https://prp-startingpoint-gtwy.apps2.edwardjones.com?locale=${LOCALE_FR_CA}`;
    }
    if (UAT_LANGUAGE_URL_REGEX.test(hostname)) {
      return 'https://startingpoint.uat.edwardjones.ca,https://pointdedepart.uat.edwardjones.ca';
    }
    if (PROD_LANGUAGE_URL_REGEX.test(hostname)) {
      return 'https://startingpoint.edwardjones.ca,https://pointdedepart.edwardjones.ca';
    }
    if (UAT_EXTERNAL_TEST_URL_REGEX.test(hostname)) {
      return `https://startingpoint-uat.testjones.net?locale=${LOCALE_EN_CA},https://startingpoint-uat.testjones.net?locale=${LOCALE_FR_CA}`;
    }
    if (PREPROD_EXTERNAL_TEST_URL_REGEX.test(hostname)) {
      return `https://startingpoint.testjones.net?locale=${LOCALE_EN_CA},https://startingpoint.testjones.net?locale=${LOCALE_FR_CA}`;
    }
    return `http://localhost:1234/ca-en/index.html?locale=${LOCALE_EN_CA},http://localhost:1234/ca-fr/index.html?locale=${LOCALE_FR_CA}`;
  };

  const getOptions = () => {
    const optionsArray = [];
    const languageSelectTexts = getText(
      locId,
      'AllPages',
      'languageSelectTexts',
      'LanguageSelect'
    ).split(',');
    const languageSelectUrls = getSelectLanguageUrls().split(',');
    const languageSelectTextDefault = getText(
      locId,
      'AllPages',
      'languageSelectTextDefault',
      'LanguageSelect'
    );

    for (let index = 0; index < languageSelectTexts.length; index++) {
      const languageText = languageSelectTexts[index];
      const languageUrl = languageSelectUrls[index];
      const option = {
        name: languageText,
        selected: languageText === languageSelectTextDefault,
        value: languageUrl,
        disabled: false,
      };
      optionsArray.push(option);
    }
    return optionsArray;
  };

  const handleCancel = () => {
    setShowConfirmDialog(false);
    setOptions(getOptions());
  };

  const displayDialog = (value) => {
    setUrl(value);
    setShowConfirmDialog(true);
  };

  const handleLeave = () => {
    setShowConfirmDialog(false);
    const queryParams = new URLSearchParams(
      DOMPurify.sanitize(window.location.search)
    );
    let newUrl = url;
    queryParams.delete('locale');
    if (Array.from(queryParams).length > 0) {
      if (url.includes('?')) {
        newUrl = url.concat('&', queryParams.toString());
      } else {
        newUrl = url.concat('?', queryParams.toString());
      }
    }
    window.location.replace(newUrl);
  };

  useEffect(() => {
    if (locId === LOCALE_EN_CA || locId === LOCALE_FR_CA) {
      setOptions(getOptions());
    }
  }, []);

  useEffect(() => {
    if (locId === LOCALE_EN_CA || locId === LOCALE_FR_CA) {
      if (showConfirmDialog) {
        removeBeforeUnloadEventListener();
      } else {
        addBeforeUnloadEventListener();
      }
    }
  }, [showConfirmDialog]);

  return locId === LOCALE_EN_CA || locId === LOCALE_FR_CA ? (
    <div className="language-select">
      <div className="language-select--select-input">
        <SelectInput
          id="language-select-input"
          label={getText(
            locId,
            'AllPages',
            'languageSelectLabel',
            'LanguageSelect'
          )}
          name="language-select-input"
          onSelect={(value) => {
            displayDialog(value);
          }}
          options={options}
        />
      </div>
      <hr aria-hidden="false" />
      {showConfirmDialog ? (
        <Dialog
          ariaDescribedBy="cancel-dialogue"
          open={showConfirmDialog}
          buttons={[
            {
              onClick: () => handleCancel(),
              text: getText(
                locId,
                'AllPages',
                'languageDialogCancel',
                'LanguageSelect'
              ),
            },
            {
              attributes: {
                value: 'continue',
              },
              onClick: () => handleLeave(),
              styleVariant: 'primary',
              text: getText(
                locId,
                'AllPages',
                'languageDialogLeave',
                'LanguageSelect'
              ),
            },
          ]}
          onRequestClose={() => {
            handleCancel();
          }}
          title=""
        >
          <div className="dialog-content">
            <div className="dialog-content--icon-container">
              <Icon name="notice-warning" />
            </div>
            <div className="dialog-content--content-container">
              <p className="dialog-content--content-container--dialog-title">
                {getText(
                  locId,
                  'AllPages',
                  'languageDialogQuestion1',
                  'LanguageSelect'
                )}
              </p>
              <span className="dialog-content--content-container--dialog-title">
                {getText(
                  locId,
                  'AllPages',
                  'languageDialogQuestion2',
                  'LanguageSelect'
                )}
              </span>
              <br />
              <br />
              <p>
                {getText(
                  locId,
                  'AllPages',
                  'languageDialogTitle1',
                  'LanguageSelect'
                )}
              </p>
              <span>
                {getText(
                  locId,
                  'AllPages',
                  'languageDialogTitle2',
                  'LanguageSelect'
                )}
              </span>
            </div>
          </div>
        </Dialog>
      ) : null}
    </div>
  ) : (
    <></>
  );
};

LanguageSelect.propTypes = {
  addBeforeUnloadEventListener: PropTypes.func.isRequired,
  removeBeforeUnloadEventListener: PropTypes.func.isRequired,
};

export default LanguageSelect;
